import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  getOrCreateReferralCode,
  getReferralStats,
  getReferralLeaderboard,
  getTierForCount,
  getTierInfo,
  getNextReferralTier,
  REFERRAL_TIERS,
} from './utils/referralManager';
import './referral.css';

// ─── Helpers ───────────────────────────────────────────────────────────────

function shortAddr(addr) {
  if (!addr) return '';
  return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
}

function formatEarned(n) {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

// ─── Main Component ────────────────────────────────────────────────────────

function ReferralModal({ isOpen, onClose, walletAddress, isToiletMode }) {
  const [tab, setTab] = useState('invite'); // 'invite' | 'leaderboard'
  const [code, setCode] = useState(null);
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  const referralUrl = code ? `https://distil.wtf?ref=${code}` : '';

  // Load code + stats on open
  useEffect(() => {
    if (!isOpen || !walletAddress) return;
    setLoading(true);
    setError(null);

    Promise.all([
      getOrCreateReferralCode(walletAddress).catch(e => ({ error: e.message })),
      getReferralStats(walletAddress).catch(() => null),
    ]).then(([codeResult, statsResult]) => {
      if (codeResult.error) {
        setError(codeResult.error);
      } else {
        setCode(codeResult.code);
      }
      setStats(statsResult);
      setLoading(false);
    });
  }, [isOpen, walletAddress]);

  // Load leaderboard when tab switches
  useEffect(() => {
    if (tab === 'leaderboard' && leaderboard.length === 0) {
      getReferralLeaderboard(50).then(setLeaderboard);
    }
  }, [tab]);

  const handleCopy = () => {
    if (!referralUrl) return;
    navigator.clipboard.writeText(referralUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const shareText = (platform) => {
    const url = referralUrl;
    const messages = {
      twitter: `I'm cleaning up my Sui wallet and earning $DISTIL! 🚽💎\n\nJoin me and get 50 $DISTIL to start:\n${url}\n\n#Sui #DeFi #WalletCleanup`,
      telegram: `Check out Distil Protocol!\n\nDelete spam from your Sui wallet and earn $DISTIL tokens.\n\nGet a 50 $DISTIL bonus when you join:\n${url}`,
      discord: `Yo! I'm using Distil to clean my Sui wallet and earn tokens.\n\nYou should try it - get 50 $DISTIL just for signing up:\n${url}\n\nLet me know if you have questions! 🚽💎`,
    };

    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(messages.twitter)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(messages.telegram)}`,
      discord: null, // Discord doesn't support direct share URL — copy instead
    };

    if (platform === 'discord') {
      navigator.clipboard.writeText(messages.discord);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else {
      window.open(urls[platform], '_blank', 'noopener,noreferrer');
    }
  };

  if (!isOpen) return null;

  const tierInfo = stats ? getTierInfo(stats.tier) : null;
  const nextTier = stats ? getNextReferralTier(stats.totalReferrals) : null;
  const progress = nextTier && stats
    ? Math.min(100, (stats.totalReferrals / nextTier.min) * 100)
    : 100;

  return (
    <AnimatePresence>
      <motion.div
        className={`referral-overlay${isToiletMode ? ' toilet-mode' : ''}`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className={`referral-modal${isToiletMode ? ' toilet-mode' : ''}`}
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 30, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="referral-header">
            <div className="referral-header-left">
              <span className="referral-header-icon">🚽</span>
              <div>
                <h2 className="referral-title">Invite Friends</h2>
                <p className="referral-subtitle">You earn 100 · They get 50 $DISTIL</p>
              </div>
            </div>
            <button className="referral-close" onClick={onClose}>✕</button>
          </div>

          {/* Tabs */}
          <div className="referral-tabs">
            <button
              className={`referral-tab${tab === 'invite' ? ' active' : ''}`}
              onClick={() => setTab('invite')}
            >
              📤 Invite
            </button>
            <button
              className={`referral-tab${tab === 'leaderboard' ? ' active' : ''}`}
              onClick={() => setTab('leaderboard')}
            >
              🏆 Leaderboard
            </button>
          </div>

          {/* ── Invite Tab ── */}
          {tab === 'invite' && (
            <div className="referral-body">
              {loading ? (
                <div className="referral-loading">
                  <div className="referral-spinner" />
                  <span>Loading your referral link...</span>
                </div>
              ) : error ? (
                <div className="referral-error">
                  <span>⚠️ {error}</span>
                </div>
              ) : (
                <>
                  {/* Link box */}
                  <div className="referral-link-box">
                    <span className="referral-link-text">{referralUrl}</span>
                    <button
                      className={`referral-copy-btn${copied ? ' copied' : ''}`}
                      onClick={handleCopy}
                    >
                      {copied ? '✓ Copied!' : '📋 Copy'}
                    </button>
                  </div>

                  {/* Share buttons */}
                  <div className="referral-share-row">
                    <button className="ref-share-btn twitter" onClick={() => shareText('twitter')}>
                      𝕏 Twitter
                    </button>
                    <button className="ref-share-btn telegram" onClick={() => shareText('telegram')}>
                      ✈️ Telegram
                    </button>
                    <button className="ref-share-btn discord" onClick={() => shareText('discord')}>
                      💬 Discord
                    </button>
                  </div>

                  {/* Stats card */}
                  {stats && (
                    <div className="referral-stats-card">
                      <div className="ref-stats-row">
                        <div className="ref-stat">
                          <span className="ref-stat-val">{stats.totalReferrals}</span>
                          <span className="ref-stat-lbl">Total</span>
                        </div>
                        <div className="ref-stat">
                          <span className="ref-stat-val">{stats.activeReferrals}</span>
                          <span className="ref-stat-lbl">Active (30d)</span>
                        </div>
                        <div className="ref-stat">
                          <span className="ref-stat-val">{formatEarned(stats.totalEarned)}</span>
                          <span className="ref-stat-lbl">$DISTIL Earned</span>
                        </div>
                      </div>

                      {/* Tier */}
                      <div className="ref-tier-row">
                        {tierInfo ? (
                          <span className="ref-tier-badge">
                            {tierInfo.emoji} {tierInfo.name}
                            <span className="ref-tier-bonus">+{(tierInfo.bonus * 100).toFixed(0)}% all earnings</span>
                          </span>
                        ) : (
                          <span className="ref-tier-badge none">
                            🤝 No tier yet — get your first referral!
                          </span>
                        )}
                      </div>

                      {/* Progress to next tier */}
                      {nextTier && (
                        <div className="ref-progress-wrap">
                          <div className="ref-progress-label">
                            <span>Next: {nextTier.emoji} {nextTier.name}</span>
                            <span>{stats.totalReferrals}/{nextTier.min}</span>
                          </div>
                          <div className="ref-progress-track">
                            <motion.div
                              className="ref-progress-fill"
                              initial={{ width: 0 }}
                              animate={{ width: `${progress}%` }}
                              transition={{ duration: 0.6, ease: 'easeOut' }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Recent referrals */}
                      {stats.recentReferrals?.length > 0 && (
                        <div className="ref-recent">
                          <p className="ref-recent-title">Recent Referrals</p>
                          {stats.recentReferrals.map((r, i) => (
                            <div key={i} className="ref-recent-row">
                              <span className="ref-recent-addr">{shortAddr(r.referred_address)}</span>
                              <span className={`ref-recent-status ${r.status}`}>
                                {r.status === 'completed' ? '✅ Qualified' : '⏳ Pending'}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Reward info */}
                  <div className="referral-reward-info">
                    <span>💎 You earn <strong>100 $DISTIL</strong> per qualified referral</span>
                    <span>🎁 They get <strong>50 $DISTIL</strong> welcome bonus</span>
                    <span>⚡ Qualifies after <strong>500 purity points</strong> earned</span>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── Leaderboard Tab ── */}
          {tab === 'leaderboard' && (
            <div className="referral-body">
              <p className="ref-lb-title">🏆 Top Referrers (All Time)</p>
              {leaderboard.length === 0 ? (
                <div className="referral-loading">
                  <div className="referral-spinner" />
                  <span>Loading...</span>
                </div>
              ) : (
                <div className="ref-lb-list">
                  {leaderboard.map((entry, i) => {
                    const rank = i + 1;
                    const isUser = entry.user_address === walletAddress;
                    const tier = getTierInfo(entry.tier);
                    const medal = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : `#${rank}`;
                    return (
                      <div key={entry.user_address} className={`ref-lb-row${isUser ? ' you' : ''}`}>
                        <span className="ref-lb-rank">{medal}</span>
                        <span className="ref-lb-addr">
                          {shortAddr(entry.user_address)}
                          {isUser && <span className="ref-lb-you">YOU</span>}
                        </span>
                        {tier && <span className="ref-lb-tier">{tier.emoji} {tier.name}</span>}
                        <span className="ref-lb-count">{entry.total_referrals} refs</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default ReferralModal;
